<template>
  <section id="choose-how-you-want-to-explore" class="ai-path" aria-labelledby="ai-path-title">
    <header class="ai-path-header">
      <span>One site, three ways to explore</span>
      <h2 id="ai-path-title">Choose how you want to explore</h2>
    </header>

    <div class="ai-path-tabs" role="tablist" aria-label="Site AI capability">
      <button
        v-for="path in paths"
        :id="`ai-path-tab-${path.id}`"
        :key="path.id"
        type="button"
        role="tab"
        :aria-selected="(active === path.id).toString()"
        :aria-controls="`ai-path-panel-${path.id}`"
        :class="{ active: active === path.id }"
        @click="active = path.id"
      >
        <span>{{ path.verb }}</span>
        <strong><AiViewTerm v-if="path.id === 'view'" /><AiAskTerm v-else-if="path.id === 'ask'" /><template v-else>{{ path.label }}</template></strong>
      </button>
    </div>

    <transition name="ai-path-panel" mode="out-in">
      <article
        :id="`ai-path-panel-${current.id}`"
        :key="current.id"
        class="ai-path-panel"
        role="tabpanel"
        :aria-labelledby="`ai-path-tab-${current.id}`"
      >
        <div class="ai-path-copy">
          <span>{{ current.kicker }}</span>
          <h3 v-if="current.id === 'ask'"><AiAskTerm /> a question and get an answer you can trace</h3>
          <h3 v-else>{{ current.title }}</h3>
          <p v-if="current.id === 'ask'"><AiAskTerm /> finds the relevant passages, writes a direct answer from them, and cites the original pages so you can check the source.</p>
          <p v-else>{{ current.summary }}</p>
          <button type="button" @click="runCurrent">
            <span v-if="current.id === 'view'">Compose a <AiViewTerm /></span>
            <span v-else-if="current.id === 'ask'"><AiAskTerm /> the site</span>
            <span v-else>{{ current.action }}</span>
            <ArrowUpRightIcon />
          </button>
        </div>

        <ol class="ai-path-steps">
          <li v-for="(step, index) in current.steps" :key="step.title">
            <span>{{ String(index + 1).padStart(2, '0') }}</span>
            <div>
              <strong>{{ step.title }}</strong>
              <p>{{ step.body }}</p>
            </div>
          </li>
        </ol>
      </article>
    </transition>
  </section>
</template>

<script>
import AiViewTerm from '../ui/AiViewTerm.vue';
import AiAskTerm from '../ui/AiAskTerm.vue';
import ArrowUpRightIcon from '../ui/ArrowUpRightIcon.vue';

export default {
  name: 'SiteAiPathExplorer',
  components: { AiAskTerm, AiViewTerm, ArrowUpRightIcon },
  data() {
    return {
      active: 'search',
      paths: [
        {
          id: 'search',
          verb: 'Find',
          label: 'Search',
          kicker: 'Across the whole site',
          title: 'Search once and land on the right passage',
          summary: 'Search moves across every page, shows the relevant passage, highlights the match, and opens the original material at its precise location. It compares both shared wording and shared meaning.',
          action: 'Try Search',
          event: 'open-search',
          steps: [
            { title: 'Represent meaning', body: 'Turn every passage and the question into coordinates that can be compared by meaning.' },
            { title: 'Compare', body: 'Look for both matching words and related meaning across the whole site.' },
            { title: 'Combine', body: 'Bring both result lists together while preserving strong name and phrase matches.' },
            { title: 'Open', body: 'Send the reader back to the original material and its precise location.' },
          ],
        },
        {
          id: 'ask',
          verb: 'Answer',
          label: 'Ask',
          kicker: 'An answer with citations',
          title: 'Ask a question and get an answer you can trace',
          summary: 'Ask finds the relevant passages, writes a direct answer from them, and cites the original pages so you can check the source.',
          action: 'Ask the site',
          event: 'open-ask',
          steps: [
            { title: 'Find evidence', body: 'Collect the site material most relevant to the visitor’s question.' },
            { title: 'Set the scope', body: 'Give the answer model a clear set of passages it can use.' },
            { title: 'Write', body: 'Turn those passages into a direct answer.' },
            { title: 'Cite', body: 'Connect the response back to the original pages it used.' },
          ],
        },
        {
          id: 'view',
          verb: 'Compose',
          label: 'View',
          kicker: 'A new reading path',
          title: 'Turn an angle into a page built from original material',
          summary: 'The composer gathers material from across the site, connects it around one angle, and places original components beside the narrative. The finished page is saved with its creation date and sources.',
          action: 'Compose',
          event: 'compose-view',
          steps: [
            { title: 'Gather', body: 'Find a broader set of material that can support the chosen angle.' },
            { title: 'Plan', body: 'Select the useful sources and arrange them into a reading sequence.' },
            { title: 'Compose', body: 'Write the narrative and place original site components where they add evidence.' },
            { title: 'Save', body: 'Keep the finished View, its creation date, and its sources in a shareable link.' },
          ],
        },
      ],
    };
  },
  computed: {
    current() {
      return this.paths.find((path) => path.id === this.active) || this.paths[0];
    },
  },
  methods: {
    runCurrent() {
      if (this.current.id === 'search') {
        window.dispatchEvent(new CustomEvent('open-site-search'));
        return;
      }
      if (this.current.id === 'ask') {
        window.dispatchEvent(new CustomEvent('open-site-ask'));
        return;
      }
      window.dispatchEvent(new CustomEvent('open-site-view'));
    },
  },
};
</script>

<style scoped>
.ai-path {
  margin: 1.8rem 0 2.8rem;
  background: rgba(var(--site-accent-rgb), 0.065);
  color: #343434;
  font-family: "Quattrocento Sans", sans-serif;
}
.ai-path-header { padding: 1.15rem 1.2rem 0.9rem; }
.ai-path-header > span,
.ai-path-copy > span {
  color: var(--site-accent);
  font-size: 0.67rem;
  font-weight: 600;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}
.ai-path-header h2 {
  margin: 0.32rem 0 0;
  font-size: 1.28rem;
  font-weight: 600;
  line-height: 1.25;
}
.ai-path-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.35rem;
  padding: 0 1.2rem;
}
.ai-path-tabs button {
  position: relative;
  padding: 0.72rem 0.75rem 0.78rem;
  border: 0;
  border-radius: 0;
  background: rgba(var(--site-accent-rgb), 0.055);
  color: #777;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 160ms ease, color 160ms ease, transform 100ms ease-out;
}
.ai-path-tabs button::after {
  position: absolute;
  right: 0.75rem;
  bottom: 0.5rem;
  left: 0.75rem;
  height: 2px;
  background: var(--site-accent);
  content: '';
  opacity: 0;
  transform: scaleX(0.4);
  transform-origin: left;
  transition: opacity 160ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.ai-path-tabs button:hover,
.ai-path-tabs button:focus-visible { background: rgba(var(--site-accent-rgb), 0.105); color: #343434; }
.ai-path-tabs button:focus-visible { outline: 1px solid currentColor; outline-offset: 2px; }
.ai-path-tabs button:active { transform: scale(0.985); }
.ai-path-tabs button.active { background: rgba(var(--site-accent-rgb), 0.13); color: #343434; }
.ai-path-tabs button.active::after { opacity: 1; transform: scaleX(1); }
.ai-path-tabs span,
.ai-path-tabs strong { display: block; }
.ai-path-tabs span { margin-bottom: 0.18rem; font-size: 0.66rem; letter-spacing: 0.04em; }
.ai-path-tabs strong { padding-bottom: 0.35rem; font-size: 0.88rem; font-weight: 600; }
.ai-path-panel {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: clamp(1.5rem, 5vw, 3rem);
  padding: 1.5rem 1.2rem 1.3rem;
}
.ai-path-copy h3 { margin: 0.38rem 0 0; font-size: 1.28rem; font-weight: 600; line-height: 1.28; }
.ai-path-copy p { margin: 0.68rem 0 0; color: #6f6f6f; font-size: 0.84rem; line-height: 1.55; }
.ai-path-copy button {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  margin-top: 1rem;
  padding: 0.62rem 0.7rem;
  border: 0;
  border-radius: 0;
  background: rgba(var(--site-accent-rgb), 0.14);
  color: #343434;
  font: 600 0.76rem/1 "Quattrocento Sans", sans-serif;
  cursor: pointer;
  transition: background-color 160ms ease, transform 100ms ease-out;
}
.ai-path-copy button .site-arrow-up-right { color: var(--site-accent); transition: transform 160ms ease; }
.ai-path-copy button:hover,
.ai-path-copy button:focus-visible { background: rgba(var(--site-accent-rgb), 0.23); }
.ai-path-copy button:hover .site-arrow-up-right,
.ai-path-copy button:focus-visible .site-arrow-up-right { transform: translate(2px, -2px); }
.ai-path-copy button:focus-visible { outline: 1px solid currentColor; outline-offset: 3px; }
.ai-path-copy button:active { transform: scale(0.98); }
.ai-path-steps { margin: 0; padding: 0; list-style: none; }
.ai-path-steps li { display: grid; grid-template-columns: 1.65rem minmax(0, 1fr); gap: 0.55rem; padding: 0.6rem 0; }
.ai-path-steps li + li { box-shadow: inset 0 1px rgba(var(--site-accent-rgb), 0.13); }
.ai-path-steps li > span { padding-top: 0.08rem; color: var(--site-accent); font-size: 0.62rem; letter-spacing: 0.05em; }
.ai-path-steps strong { font-size: 0.78rem; font-weight: 600; }
.ai-path-steps p { margin: 0.16rem 0 0; color: #777; font-size: 0.72rem; line-height: 1.45; }
.ai-path-panel-enter-active,
.ai-path-panel-leave-active { transition: opacity 140ms ease, transform 180ms cubic-bezier(0.22, 1, 0.36, 1); }
.ai-path-panel-enter-from { opacity: 0; transform: translateY(6px); }
.ai-path-panel-leave-to { opacity: 0; transform: translateY(-3px); }
:global(body.dark-mode .ai-path) { background: rgba(var(--site-accent-rgb), 0.075); color: #dedede; }
:global(body.dark-mode .ai-path .ai-path-tabs button) { background: rgba(var(--site-accent-rgb), 0.06); color: var(--site-text-secondary); }
:global(body.dark-mode .ai-path .ai-path-tabs button:hover),
:global(body.dark-mode .ai-path .ai-path-tabs button:focus-visible),
:global(body.dark-mode .ai-path .ai-path-tabs button.active) { background: rgba(var(--site-accent-rgb), 0.145); color: #e2e2e2; }
:global(body.dark-mode .ai-path .ai-path-copy p),
:global(body.dark-mode .ai-path .ai-path-steps p) { color: var(--site-text-secondary); }
:global(body.dark-mode .ai-path .ai-path-copy button) { background: rgba(var(--site-accent-rgb), 0.15); color: #e2e2e2; }
:global(body.dark-mode .ai-path .ai-path-copy button:hover),
:global(body.dark-mode .ai-path .ai-path-copy button:focus-visible) { background: rgba(var(--site-accent-rgb), 0.24); }
@media (max-width: 620px) {
  .ai-path-header { padding-inline: 1rem; }
  .ai-path-tabs { gap: 0.2rem; padding-inline: 1rem; }
  .ai-path-tabs button { padding-inline: 0.55rem; }
  .ai-path-tabs button::after { right: 0.55rem; left: 0.55rem; }
  .ai-path-panel { grid-template-columns: 1fr; gap: 1.15rem; padding-inline: 1rem; }
}
@media (prefers-reduced-motion: reduce) {
  .ai-path-tabs button,
  .ai-path-tabs button::after,
  .ai-path-copy button,
  .ai-path-copy button .site-arrow-up-right,
  .ai-path-panel-enter-active,
  .ai-path-panel-leave-active { transition-duration: 0.01ms; }
  .ai-path-panel-enter-from,
  .ai-path-panel-leave-to { transform: none; }
}
</style>
